<template>
  <div>
    <div class="overflow-auto">
      <ve-table
        max-height="calc(100vh - 16.000rem)"
        scroll-width="50rem"
        row-key-field-name="id"
        :columns="columns"
        :table-data="data"
        :border-around="false"
        :checkbox-option="checkboxOption"
        :row-style-option="getRowStyleOption()"
        :expand-option="expandOption"
      />
    </div>
  </div>
</template>

<script>
import { VeTable } from 'vue-easytable';
import Message from './Message.vue';

export default {
  components: {
    Message,
    VeTable,
  },
  data() {
    return {
      expandOption: {
        render: ({ row, column, rowIndex }, h) => {
          return h(Message, {
            props: {
              headerImage: this.getHeaderImage(row),
              contentMessages: this.getContentMessages(row),
              buttonMessages: this.getButtonMessages(row),
              isButtonMessages: this.getIsButtonMessages(row),
            },
          });
        },
      },
    };
  },
  props: {
    data: {
      type: Array,
      default: [],
    },
    meta: {
      type: Object,
      default: {},
    },
    selectedRowKeys: {
      type: Array,
      default: () => [],
    },
  },
  methods: {
    mergeSelection(newKeys) {
      const currentPageIds = this.data.map(row => row.id);
      const filteredOldKeys = this.selectedRowKeys.filter(
        id => !currentPageIds.includes(id)
      );

      return [...new Set([...filteredOldKeys, ...newKeys])];
    },
    getRowStyleOption() {
      return {
        clickHighlight: false,
        stripe: false,
      };
    },
    getHeaderImage(row) {
      const header = row?.template_data?.components?.find(
        c => c.type === 'HEADER' && c.format === 'IMAGE'
      );
      return header?.example?.header_handle?.[0] || null;
    },
    getContentMessages(row) {
      const body = row?.params?.message?.content;

      return body || '';
    },
    getButtonMessages(row) {
      const buttons = row?.template_data?.components?.find(
        c => c.type === 'BUTTONS'
      );
      return Array.isArray(buttons?.buttons) ? buttons.buttons : [];
    },
    getIsButtonMessages(row) {
      return this.getButtonMessages(row).length > 0;
    },
  },
  computed: {
    checkboxOption() {
      return {
        hideSelectAll: true,
        selectedRowKeys: this.selectedRowKeys,
        selectedRowChange: ({ selectedRowKeys }) => {
          this.$emit('updateRows', this.mergeSelection(selectedRowKeys));
        },
      };
    },
    columns() {
      return [
        {
          field: 'index',
          key: 'index',
          title: 'No',
          width: 10,
          align: 'center',
          fixed: 'left',
          renderBodyCell: ({ rowIndex }) => {
            return (
              (this.meta.currentPage - 1) * this.meta.perPage + (rowIndex + 1)
            );
          },
        },
        {
          field: '',
          key: 'checkbox',
          type: 'checkbox',
          title: '',
          fixed: 'left',
          width: 10,
          align: 'center',
        },
        {
          field: '',
          key: 'a',
          type: 'expand',
          title: '',
          width: 10,
          align: 'center',
        },
        {
          field: 'name',
          key: 'name',
          title: this.$t('BROADCAST.TABLE.NAME'),
          fixed: 'left',
          width: 40,
          align: 'left',
          renderBodyCell: ({ row }) => {
            return (
              <div class="row-user">
                <div class="user-block">{row.contact.name}</div>
              </div>
            );
          },
        },
        {
          field: 'phone_number',
          key: 'phone_number',
          title: this.$t('BROADCAST.TABLE.PHONE_NUMBER'),
          width: 30,
          align: 'left',
          renderBodyCell: ({ row }) => {
            return (
              <div class="row-user">
                <div class="user-block">{row.phone_number}</div>
              </div>
            );
          },
        },
        {
          field: 'created_at',
          key: 'created_at',
          title: this.$t('BROADCAST.TABLE.CREATED_DATE'),
          width: 20,
          align: 'left',
          renderBodyCell: ({ row }) => {
            const date = new Date(row.created_at);
            const formattedDate = new Intl.DateTimeFormat('id-ID', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            }).format(date);

            const formattedTime = new Intl.DateTimeFormat('id-ID', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            }).format(date);
            return (
              <div class="row-user">
                <div class="user-block">
                  <div>{formattedDate}</div>
                  <div>{formattedTime}</div>
                </div>
              </div>
            );
          },
        },
        {
          field: 'status',
          key: 'status',
          title: this.$t('BROADCAST.TABLE.STATUS'),
          width: 30,
          align: 'center',
          renderBodyCell: ({ row }) => {
            let icon;
            switch (row.status.name) {
              case 'Pending':
                icon = (
                  <div class="tags">
                    <span class="pending-tag">
                      {this.$t('BROADCAST.STATUS.PENDING')}
                    </span>
                  </div>
                );
                break;
              case 'Approved':
                icon = (
                  <div class="tags">
                    <span class="finish-tag">
                      {this.$t('BROADCAST.STATUS.APPROVED')}
                    </span>
                  </div>
                );
                break;
              default:
                icon = (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    class="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 6v6h6"
                    />
                  </svg>
                );
            }
            return (
              <div
                class="row-user"
                style="display: flex; justify-content: center; align-items: center; height: 100%;"
              >
                <div class="user-block">
                  <span>{icon}</span>
                </div>
              </div>
            );
          },
        },
      ];
    },
  },
};
</script>

<style scoped>
.row-user {
  @apply items-center flex text-left my-4;
}

.row-date {
  @apply items-center flex text-center my-4 justify-center;
}

.pending-tag {
  @apply bg-yellow-600 text-white text-xs py-0.5 px-3 rounded-md;
}

.finish-tag {
  @apply bg-green-600 text-white text-xs py-0.5 px-3 rounded-md;
}

::v-deep .ve-table-expand-td-content {
  padding: 0 !important;
}

::v-deep(.ve-table-expand-tr > .ve-table-expand-td) {
  background-color: #aeaeae !important;
  padding: 1rem !important;
}
</style>
